import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  FiSave, 
  FiUpload, 
  FiHelpCircle, 
  FiX, 
  FiCheck,
  FiDroplet,
  FiImage,
  FiTrash2,
  FiRefreshCw
} from 'react-icons/fi';

// Get API URL from environment variable
const API_URL = process.env.REACT_APP_API_URL || 'https://api.tinubu-ambode.org';

// API Service Functions with better error handling
const brandingAPI = {
  // Get branding data
  getBranding: async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      console.log('Fetching branding from:', `${API_URL}/api/v1/admin/branding`);
      console.log('Using token:', authToken ? 'Token exists' : 'No token');
      
      const response = await fetch(`${API_URL}/api/v1/admin/branding`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies if needed
      });
      
      console.log('Branding response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Branding error response:', errorText);
        throw new Error(`HTTP ${response.status}: Method Not Allowed - Check endpoint permissions`);
      }
      
      const data = await response.json();
      console.log('Branding data loaded:', data);
      return data;
    } catch (error) {
      console.error('Error fetching branding:', error);
      throw error;
    }
  },

  // Upload logo
  uploadLogo: async (logoFile) => {
    try {
      const authToken = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('logo', logoFile);

      console.log('Uploading logo to:', `${API_URL}/api/v1/admin/branding/logo`);

      const response = await fetch(`${API_URL}/api/v1/admin/branding/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          // Don't set Content-Type for FormData, let browser set it
        },
        body: formData,
        credentials: 'include',
      });
      
      console.log('Upload logo response:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  },

  // Update branding colors
  updateColors: async (colors) => {
    try {
      const authToken = localStorage.getItem('authToken');
      console.log('Updating colors at:', `${API_URL}/api/v1/admin/branding/colors`);
      console.log('Colors to update:', colors);
      
      const response = await fetch(`${API_URL}/api/v1/admin/branding/colors`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          primary_color: colors.primary_color,
          secondary_color: colors.secondary_color,
          accent_color: colors.accent_color,
        }),
        credentials: 'include',
      });
      
      console.log('Update colors response:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Update colors error:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating colors:', error);
      throw error;
    }
  },

  // Delete logo
  deleteLogo: async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      console.log('Deleting logo at:', `${API_URL}/api/v1/admin/branding/logo`);
      
      const response = await fetch(`${API_URL}/api/v1/admin/branding/logo`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      console.log('Delete logo response:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting logo:', error);
      throw error;
    }
  },
};

// --- Success Modal Component ---
const SuccessModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modalContent}>
        <div style={styles.modalIcon}>
          <FiCheck size={48} color="#10B981" />
        </div>
        <h3 style={styles.modalTitle}>Successfully Saved!</h3>
        <p style={styles.modalText}>
          Your brand identity changes have been saved successfully.
        </p>
        <button 
          onClick={onClose}
          style={styles.modalButton}
          className="modal-button"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

// --- Error Modal Component ---
const ErrorModal = ({ isOpen, onClose, errorMessage, onRetry }) => {
  if (!isOpen) return null;

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modalContent}>
        <div style={styles.modalIcon}>
          <FiX size={48} color="#EF4444" />
        </div>
        <h3 style={styles.modalTitle}>Error</h3>
        <p style={styles.modalText}>
          {errorMessage || 'An error occurred while saving your changes.'}
        </p>
        <div style={styles.modalButtons}>
          <button 
            onClick={onClose}
            style={styles.modalButtonSecondary}
            className="modal-button"
          >
            Close
          </button>
          {onRetry && (
            <button 
              onClick={onRetry}
              style={styles.modalButton}
              className="modal-button"
            >
              <FiRefreshCw size={16} />
              Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Loading Spinner Component ---
const LoadingSpinner = () => (
  <div style={styles.loadingOverlay}>
    <div style={styles.spinner}></div>
    <p style={styles.loadingText}>Loading branding data...</p>
  </div>
);

// --- Logo Uploader Component ---
const LogoUploader = ({ logo, setLogo, onLogoUpload, onLogoDelete, isLoading }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (file && file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      // Create preview first
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result);
      };
      reader.readAsDataURL(file);

      // Upload logo to server
      try {
        await onLogoUpload(file);
      } catch (error) {
        console.error('Error uploading logo:', error);
        setLogo(null);
        throw error;
      }
    }
  }, [setLogo, onLogoUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFile(file);
    }
  };
  
  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveLogo = async () => {
    if (window.confirm('Are you sure you want to remove the logo?')) {
      try {
        await onLogoDelete();
        setLogo(null);
      } catch (error) {
        console.error('Error deleting logo:', error);
        throw error;
      }
    }
  };

  const uploaderStyles = {
    ...styles.logoUploader,
    ...(isDragging ? styles.logoUploaderDragging : {}),
    ...(isLoading ? styles.logoUploaderLoading : {}),
  };

  return (
    <div 
      style={uploaderStyles}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className="logo-uploader"
    >
      {isLoading ? (
        <div style={styles.uploaderContent}>
          <div style={styles.spinnerSmall}></div>
          <p style={styles.uploaderText}>Uploading logo...</p>
        </div>
      ) : logo ? (
        <>
          <div style={styles.logoPreviewContainer}>
            <img src={logo} alt="Organization Logo Preview" style={styles.logoPreview} />
          </div>
          <div style={styles.logoActions}>
            <button 
              onClick={handleRemoveLogo} 
              style={styles.removeLogoButton} 
              className="remove-logo-button"
              disabled={isLoading}
            >
              <FiTrash2 size={14} />
              Remove Logo
            </button>
            <button 
              onClick={openFileDialog} 
              style={styles.uploadLogoButton} 
              className="upload-logo-button"
              disabled={isLoading}
            >
              <FiImage size={14} />
              Change Logo
            </button>
          </div>
        </>
      ) : (
        <>
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleChange}
            accept="image/*"
            style={{ display: 'none' }}
            disabled={isLoading}
          />
          <div style={styles.uploaderContent}>
            <div style={styles.uploadIcon}>
              <FiUpload size={32} color="#3B82F6" />
            </div>
            <p style={styles.uploaderText}>
              <span 
                style={styles.uploaderLink} 
                onClick={openFileDialog}
                className="upload-link"
              >
                Drop your logo here or click to browse
              </span>
            </p>
            <p style={styles.uploaderSubtext}>
              Supports PNG, JPG, SVG up to 5MB
            </p>
          </div>
          <button 
            onClick={openFileDialog} 
            style={styles.uploadLogoButton} 
            className="upload-logo-button"
            disabled={isLoading}
          >
            <FiImage size={14} />
            Upload Logo
          </button>
        </>
      )}
    </div>
  );
};

// --- Color Picker Component ---
const ColorPicker = ({ color, onChange, label, colorKey }) => {
  const handleColorChange = (e) => {
    onChange(colorKey, e.target.value);
  };

  const handleTextChange = (e) => {
    const value = e.target.value.replace(/[^a-fA-F0-9]/g, '').slice(0, 6);
    onChange(colorKey, '#' + value);
  };

  const handlePresetClick = (presetColor) => {
    onChange(colorKey, presetColor);
  };

  return (
    <div style={styles.colorPickerWrapper}>
      <div style={styles.colorPickerHeader}>
        <FiDroplet size={18} color="#6B7280" />
        <h3 style={styles.h3}>{label}</h3>
      </div>
      <div style={styles.colorPickerContainer} className="color-picker-container">
        <label htmlFor={`color-picker-${colorKey}`} style={styles.colorLabel}>
          <div 
            style={{...styles.colorPreview, backgroundColor: color }}
            className="color-preview"
          >
            <div style={styles.colorPreviewOverlay}>
              <FiDroplet size={16} color="white" />
            </div>
          </div>
          <input
            id={`color-picker-${colorKey}`}
            type="color"
            value={color}
            onChange={handleColorChange}
            style={styles.colorInput}
            className="color-input"
          />
        </label>
        <div style={styles.colorInputGroup}>
          <span style={styles.colorHash}>#</span>
          <input 
            type="text"
            value={color.replace('#', '')}
            onChange={handleTextChange}
            style={styles.textInput}
            className="color-input-text"
            maxLength={6}
            placeholder="FFFFFF"
          />
        </div>
      </div>
      <div style={styles.colorPresets}>
        <p style={styles.presetLabel}>Quick picks:</p>
        <div style={styles.presetGrid} className="preset-grid">
          {['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16', '#F97316', '#6366F1', '#14B8A6', '#78716C'].map(presetColor => (
            <button
              key={presetColor}
              style={{...styles.colorPreset, backgroundColor: presetColor}}
              onClick={() => handlePresetClick(presetColor)}
              className="color-preset"
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// --- Floating Save Button Component ---
const FloatingSaveButton = ({ onClick, isLoading, hasChanges }) => {
  return (
    <div style={styles.floatingButtonContainer}>
      <button 
        onClick={onClick}
        style={{
          ...styles.floatingSaveButton,
          ...(!hasChanges ? styles.floatingSaveButtonDisabled : {})
        }}
        className="floating-save-button"
        disabled={isLoading || !hasChanges}
      >
        {isLoading ? (
          <>
            <div style={styles.spinnerSmall}></div>
            Saving...
          </>
        ) : (
          <>
            <FiSave size={16} />
            Save Changes
          </>
        )}
      </button>
    </div>
  );
};

// --- Main App Component ---
const ManageBrandIdentity = () => {
  const [logo, setLogo] = useState(null);
  const [colors, setColors] = useState({
    primary_color: '#3B82F6',
    secondary_color: '#8B5CF6',
    accent_color: '#10B981'
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Load branding data on component mount
  useEffect(() => {
    loadBrandingData();
  }, []);

  const loadBrandingData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      
      console.log('API URL:', API_URL);
      console.log('Starting to load branding data...');
      
      // Check if auth token exists
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authentication token found. Please log in.');
      }
      
      const brandingData = await brandingAPI.getBranding();
      console.log('Branding data loaded:', brandingData);
      
      // Update state with fetched data
      // Assuming API returns { success: true, data: { ... } }
      if (brandingData && brandingData.data) {
        if (brandingData.data.logo_url) {
          setLogo(brandingData.data.logo_url);
        }
        
        setColors({
          primary_color: brandingData.data.primary_color || '#3B82F6',
          secondary_color: brandingData.data.secondary_color || '#8B5CF6',
          accent_color: brandingData.data.accent_color || '#10B981',
        });
      }
      
      setInitialLoadComplete(true);
    } catch (error) {
      console.error('Error loading branding data:', error);
      let message = error.message || 'Failed to load branding data. ';
      
      if (error.message.includes('405')) {
        message = 'API endpoint does not allow GET method. Please check server configuration.';
      } else if (error.message.includes('401')) {
        message = 'Please log in to access branding settings.';
      } else if (error.message.includes('403')) {
        message = 'You do not have permission to access branding settings.';
      } else if (error.message.includes('404')) {
        message = 'Branding endpoint not found. Please check the API URL.';
      } else if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
        message = 'Network error. Please check your internet connection and ensure API server is running.';
      }
      
      setErrorMessage(message);
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleColorChange = (colorKey, value) => {
    setColors(prev => ({
      ...prev,
      [colorKey]: value
    }));
    setHasUnsavedChanges(true);
  };

  const handleLogoUpload = async (logoFile) => {
    try {
      setIsSaving(true);
      const response = await brandingAPI.uploadLogo(logoFile);
      
      // Update logo URL from response if available
      if (response.data && response.data.logo_url) {
        setLogo(response.data.logo_url);
      }
      
      setHasUnsavedChanges(true);
      return response;
    } catch (error) {
      let message = 'Failed to upload logo. ';
      if (error.message.includes('401') || error.message.includes('403')) {
        message += 'Please check your permissions.';
      } else if (error.message.includes('NetworkError')) {
        message += 'Network error. Please check your connection.';
      } else {
        message += error.message;
      }
      setErrorMessage(message);
      setShowErrorModal(true);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoDelete = async () => {
    try {
      setIsSaving(true);
      await brandingAPI.deleteLogo();
      setLogo(null);
      setHasUnsavedChanges(true);
    } catch (error) {
      let message = 'Failed to delete logo. ';
      if (error.message.includes('401') || error.message.includes('403')) {
        message += 'Please check your permissions.';
      } else if (error.message.includes('NetworkError')) {
        message += 'Network error. Please check your connection.';
      } else {
        message += error.message;
      }
      setErrorMessage(message);
      setShowErrorModal(true);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);
      
      // Update colors using the colors endpoint
      const response = await brandingAPI.updateColors(colors);
      console.log('Colors updated:', response);
      
      setShowSuccessModal(true);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving changes:', error);
      let message = 'Failed to save changes. ';
      
      if (error.message.includes('401') || error.message.includes('403')) {
        message += 'Please check your permissions.';
      } else if (error.message.includes('NetworkError')) {
        message += 'Network error. Please check your connection.';
      } else {
        message += error.message;
      }
      
      setErrorMessage(message);
      setShowErrorModal(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setShowErrorModal(false);
  };

  const handleRetry = () => {
    setShowErrorModal(false);
    loadBrandingData();
  };

  if (isLoading && !initialLoadComplete) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <style>{`
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif;
          margin: 0;
          background: #F8FAFC;
        }

        .floating-save-button:hover:not(:disabled) {
          background-color: #1D4ED8;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(37, 99, 235, 0.3);
        }

        .save-changes-button:hover:not(:disabled) {
          background-color: #1D4ED8;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
        }

        .remove-logo-button:hover:not(:disabled) {
          background-color: #FECACA;
          transform: translateY(-1px);
        }

        .upload-logo-button:hover:not(:disabled) {
          background-color: #E5E7EB;
          transform: translateY(-1px);
        }

        .upload-link:hover {
          color: #1D4ED8;
        }

        .color-input:focus, .color-input-text:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          border-color: #3B82F6;
        }

        .color-preset:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .color-preview:hover {
          transform: scale(1.05);
        }

        .modal-button:hover {
          background-color: #1D4ED8;
          transform: translateY(-1px);
        }

        .logo-uploader:hover {
          border-color: #9CA3AF;
        }

        button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none !important;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Mobile Responsive Styles */
        @media (max-width: 1024px) {
          .main-content {
            grid-template-columns: 1fr !important;
            gap: 1.5rem !important;
          }
          
          .color-section {
            position: static !important;
          }
        }

        @media (max-width: 768px) {
          .header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }
          
          .header-actions {
            width: 100%;
            justify-content: space-between;
          }
          
          .save-changes-button {
            min-width: 140px;
          }
          
          .color-picker-container {
            flex-direction: column;
            align-items: stretch;
            gap: 1rem !important;
          }
          
          .color-input-group {
            width: 100%;
          }
          
          .preset-grid {
            grid-template-columns: repeat(6, 1fr) !important;
          }
          
          .logo-uploader {
            padding: 2rem 1rem !important;
            min-height: 240px !important;
          }
        }

        @media (max-width: 640px) {
          .page {
            padding: 1rem !important;
          }
          
          .section-padding {
            padding: 1.5rem !important;
          }
          
          .preset-grid {
            grid-template-columns: repeat(4, 1fr) !important;
            gap: 0.75rem !important;
          }
          
          .floating-save-button {
            right: 1rem !important;
            bottom: 1rem !important;
          }
        }

        @media (max-width: 480px) {
          .page-title {
            font-size: 1.5rem !important;
          }
          
          .h2 {
            font-size: 1.125rem !important;
          }
          
          .header-actions {
            flex-direction: column;
            gap: 0.75rem;
          }
          
          .save-changes-button {
            width: 100%;
            justify-content: center;
          }
          
          .preset-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
          
          .color-preset {
            width: 36px !important;
            height: 36px !important;
          }
        }
      `}</style>
      
      <div style={styles.page}>
        {(isLoading && !initialLoadComplete) && <LoadingSpinner />}
        
        <div style={styles.container}>
          {/* Header */}
          <header style={styles.header} className="header">
            <div style={styles.headerText}>
              <h1 style={styles.h1} className="page-title">Manage Brand Identity</h1>
              <p style={styles.subtitle}>Customize your organization's visual identity and branding</p>
              {hasUnsavedChanges && (
                <div style={styles.unsavedChanges}>
                  <span style={styles.unsavedDot}>•</span>
                  You have unsaved changes
                </div>
              )}
              {!initialLoadComplete && errorMessage && (
                <div style={styles.errorBanner}>
                  <FiX size={16} />
                  {errorMessage}
                </div>
              )}
            </div>
            <div style={styles.headerActions} className="header-actions">
              <button 
                style={styles.helpButton}
                className="help-button"
                onClick={loadBrandingData}
                disabled={isLoading}
              >
                <FiRefreshCw size={16} />
                Refresh
              </button>
              <button 
                onClick={handleSaveChanges}
                style={styles.saveButton}
                className="save-changes-button"
                disabled={isSaving || !hasUnsavedChanges}
              >
                {isSaving ? (
                  <>
                    <div style={styles.spinnerSmall}></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <FiSave size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </header>

          {/* Main Content */}
          {initialLoadComplete ? (
            <main style={styles.mainContent} className="main-content">
              <div style={styles.logoSection} className="section-padding">
                <div style={styles.sectionHeader}>
                  <FiImage size={20} color="#374151" />
                  <h2 style={styles.h2}>Organization Logo</h2>
                </div>
                <p style={styles.sectionDescription}>
                  Upload your organization's logo. This will be used across the platform, in emails, documents, and the member portal. Recommended size: 300x100px.
                </p>
                <LogoUploader 
                  logo={logo} 
                  setLogo={setLogo}
                  onLogoUpload={handleLogoUpload}
                  onLogoDelete={handleLogoDelete}
                  isLoading={isSaving}
                />
                
                <div style={styles.tipsSection}>
                  <h3 style={styles.tipsTitle}>Tips for best results:</h3>
                  <ul style={styles.tipsList}>
                    <li>Use high-resolution images for better quality</li>
                    <li>PNG format with transparent background recommended</li>
                    <li>Keep file size under 5MB for faster loading</li>
                    <li>Test your logo on both light and dark backgrounds</li>
                  </ul>
                </div>
              </div>
              
              <div style={styles.colorSection} className="section-padding color-section">
                <div style={styles.sectionHeader}>
                  <FiDroplet size={20} color="#374151" />
                  <h2 style={styles.h2}>Brand Colors</h2>
                </div>
                <p style={styles.sectionDescription}>
                  Customize your organization's color scheme. These colors will be used throughout the platform.
                </p>
                
                <div style={styles.colorPickersContainer}>
                  <ColorPicker 
                    color={colors.primary_color}
                    onChange={handleColorChange}
                    label="Primary Color"
                    colorKey="primary_color"
                  />
                  
                  <ColorPicker 
                    color={colors.secondary_color}
                    onChange={handleColorChange}
                    label="Secondary Color"
                    colorKey="secondary_color"
                  />
                  
                  <ColorPicker 
                    color={colors.accent_color}
                    onChange={handleColorChange}
                    label="Accent Color"
                    colorKey="accent_color"
                  />
                </div>
                
                <div style={styles.colorTips}>
                  <h3 style={styles.tipsTitle}>Color Usage:</h3>
                  <p style={styles.colorTipText}>
                    These colors will be used for buttons, links, headers, and other interactive elements throughout your organization's portal and communications.
                  </p>
                </div>
              </div>
            </main>
          ) : (
            <div style={styles.initialLoadMessage}>
              <p>Click "Refresh" to load branding data or check your authentication.</p>
            </div>
          )}
        </div>

        {/* Floating Save Button for Mobile */}
        <FloatingSaveButton 
          onClick={handleSaveChanges} 
          isLoading={isSaving}
          hasChanges={hasUnsavedChanges}
        />

        {/* Success Modal */}
        <SuccessModal 
          isOpen={showSuccessModal} 
          onClose={handleCloseModal} 
        />

        {/* Error Modal */}
        <ErrorModal 
          isOpen={showErrorModal} 
          onClose={handleCloseModal}
          onRetry={handleRetry}
          errorMessage={errorMessage}
        />
      </div>
    </>
  );
};

// --- Internal CSS Styles Object ---
const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 50%, #F1F5F9 100%)',
    padding: '2rem 1rem',
    position: 'relative',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '2.5rem',
    gap: '1rem',
  },
  headerText: {
    flex: 1,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  h1: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1E40AF',
    margin: '0',
    marginBottom: '0.5rem',
    background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  subtitle: {
    color: '#6B7280',
    fontSize: '1rem',
    margin: '0',
    lineHeight: '1.5',
    maxWidth: '500px',
  },
  unsavedChanges: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#F59E0B',
    fontSize: '0.875rem',
    fontWeight: '500',
    marginTop: '0.5rem',
  },
  unsavedDot: {
    color: '#F59E0B',
    fontSize: '1.5rem',
    lineHeight: '1',
  },
  errorBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#DC2626',
    fontSize: '0.875rem',
    fontWeight: '500',
    marginTop: '0.5rem',
    padding: '0.5rem',
    backgroundColor: '#FEE2E2',
    borderRadius: '4px',
    border: '1px solid #FECACA',
  },
  initialLoadMessage: {
    textAlign: 'center',
    padding: '3rem',
    color: '#6B7280',
    backgroundColor: '#F9FAFB',
    borderRadius: '12px',
    border: '1px dashed #D1D5DB',
  },
  helpButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1.25rem',
    backgroundColor: 'transparent',
    color: '#6B7280',
    fontWeight: '500',
    fontSize: '0.875rem',
    borderRadius: '8px',
    border: '1px solid #D1D5DB',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
  },
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1.5rem',
    backgroundColor: '#2563EB',
    color: 'white',
    fontWeight: '600',
    fontSize: '0.875rem',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.15)',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
  },
  mainContent: {
    display: 'grid',
    gridTemplateColumns: '1fr 400px',
    gap: '2.5rem',
    alignItems: 'start',
  },
  logoSection: {
    background: '#FFFFFF',
    padding: '2rem',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid #E5E7EB',
  },
  colorSection: {
    background: '#FFFFFF',
    padding: '2rem',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid #E5E7EB',
    position: 'sticky',
    top: '2rem',
  },
  colorPickersContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '0.5rem',
  },
  h2: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#374151',
    margin: '0',
  },
  h3: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#374151',
    margin: '0',
  },
  sectionDescription: {
    color: '#6B7280',
    fontSize: '0.875rem',
    marginBottom: '1.5rem',
    lineHeight: '1.5',
  },
  logoUploader: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: '3rem 2rem',
    border: '2px dashed #D1D5DB',
    borderRadius: '12px',
    backgroundColor: '#F9FAFB',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
    minHeight: '280px',
  },
  logoUploaderDragging: {
    borderColor: '#3B82F6',
    backgroundColor: '#EFF6FF',
    transform: 'scale(1.02)',
  },
  logoUploaderLoading: {
    opacity: 0.7,
    pointerEvents: 'none',
  },
  uploaderContent: {
    textAlign: 'center',
  },
  uploadIcon: {
    marginBottom: '1rem',
  },
  logoPreviewContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
  },
  logoPreview: {
    maxHeight: '120px',
    maxWidth: '100%',
    objectFit: 'contain',
    borderRadius: '8px',
  },
  logoActions: {
    display: 'flex',
    gap: '1rem',
    marginTop: '1rem',
  },
  removeLogoButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1.5rem',
    fontSize: '0.875rem',
    color: '#DC2626',
    backgroundColor: '#FEE2E2',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontWeight: '500',
  },
  uploaderText: {
    marginBottom: '0.5rem',
    fontSize: '1rem',
    color: '#374151',
    fontWeight: '500',
  },
  uploaderSubtext: {
    fontSize: '0.75rem',
    color: '#9CA3AF',
    margin: '0',
  },
  uploaderLink: {
    color: '#3B82F6',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'color 0.2s ease',
  },
  uploadLogoButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1.5rem',
    fontSize: '0.875rem',
    color: '#374151',
    backgroundColor: '#F3F4F6',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontWeight: '500',
  },
  colorPickerWrapper: {
    width: '100%',
  },
  colorPickerHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  colorPickerContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '1rem',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    backgroundColor: '#F9FAFB',
    marginBottom: '1.5rem',
  },
  colorLabel: {
    position: 'relative',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  colorPreview: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    border: '2px solid #E5E7EB',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  colorPreviewOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.2s ease',
  },
  colorInput: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: 'pointer',
  },
  colorInputGroup: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    position: 'relative',
  },
  colorHash: {
    position: 'absolute',
    left: '12px',
    color: '#6B7280',
    fontWeight: '500',
    zIndex: 1,
  },
  textInput: {
    width: '100%',
    padding: '0.75rem 0.75rem 0.75rem 2rem',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '0.875rem',
    backgroundColor: '#FFFFFF',
    fontFamily: 'monospace',
    fontWeight: '500',
  },
  colorPresets: {
    marginTop: '1rem',
  },
  presetLabel: {
    fontSize: '0.875rem',
    color: '#6B7280',
    marginBottom: '0.75rem',
    fontWeight: '500',
  },
  presetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: '0.5rem',
  },
  colorPreset: {
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    border: '2px solid #E5E7EB',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  tipsSection: {
    marginTop: '2rem',
    padding: '1.5rem',
    backgroundColor: '#F8FAFC',
    borderRadius: '8px',
    border: '1px solid #E2E8F0',
  },
  colorTips: {
    marginTop: '2rem',
    padding: '1.5rem',
    backgroundColor: '#F8FAFC',
    borderRadius: '8px',
    border: '1px solid #E2E8F0',
  },
  tipsTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#374151',
    margin: '0 0 0.75rem 0',
  },
  tipsList: {
    margin: 0,
    paddingLeft: '1.25rem',
    color: '#6B7280',
    fontSize: '0.875rem',
    lineHeight: '1.6',
  },
  colorTipText: {
    margin: 0,
    color: '#6B7280',
    fontSize: '0.875rem',
    lineHeight: '1.6',
  },
  // Floating Button Styles
  floatingButtonContainer: {
    position: 'fixed',
    bottom: '2rem',
    right: '2rem',
    zIndex: 100,
  },
  floatingSaveButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '1rem 1.5rem',
    backgroundColor: '#2563EB',
    color: 'white',
    fontWeight: '600',
    fontSize: '0.875rem',
    borderRadius: '50px',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
  },
  floatingSaveButtonDisabled: {
    backgroundColor: '#9CA3AF',
    cursor: 'not-allowed',
    transform: 'none',
    boxShadow: 'none',
  },
  // Loading Spinner Styles
  loadingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #E5E7EB',
    borderTop: '4px solid #3B82F6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  spinnerSmall: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '1rem',
    color: '#6B7280',
    fontSize: '1rem',
  },
  // Modal Styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modalContent: {
    backgroundColor: 'white',
    padding: '2.5rem',
    borderRadius: '16px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    textAlign: 'center',
    maxWidth: '400px',
    width: '100%',
  },
  modalIcon: {
    marginBottom: '1.5rem',
  },
  modalTitle: {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 0.75rem 0',
  },
  modalText: {
    fontSize: '1rem',
    color: '#6B7280',
    margin: '0 0 2rem 0',
    lineHeight: '1.5',
  },
  modalButtons: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
  },
  modalButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.875rem 1.5rem',
    backgroundColor: '#2563EB',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flex: 1,
  },
  modalButtonSecondary: {
    padding: '0.875rem 1.5rem',
    backgroundColor: '#6B7280',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flex: 1,
  },
};

export default ManageBrandIdentity;